/**
 * Real pages closest to a URL that 404'd, for the not-found page's
 * "Did you mean" list.
 *
 * Why this exists: in the 28 days to 2026-09-20, 474 URLs that Google showed
 * in results returned 404. Almost all are near-misses of real slugs, many of
 * them invented by AI answer engines ("stanley-venv-yeti-venv-hydroflask-...",
 * "do-zioloc-bags-release-microplastics"). The clear, higher-traffic ones get
 * explicit redirects in next.config.js; this covers the long tail without
 * guessing: a person picks from the suggestions, and the page stays a 404.
 */
export interface PageCandidate {
  href: string
  title: string
}

export interface ScoredCandidate extends PageCandidate {
  score: number
}

// Words so common across our slugs that matching on them says nothing.
const STOPWORDS = new Set([
  'blog', 'the', 'a', 'an', 'of', 'and', 'for', 'in', 'is', 'are', 'do', 'does', 'to', 'vs', 'v',
  'your', 'with', 'or', 'on', 'microplastics', 'microplastic', 'microplasticsapp', 'com', 'www',
  'https', 'http', 'html',
])

export function slugTokens(path: string): string[] {
  let decoded = path
  try {
    decoded = decodeURIComponent(path)
  } catch {
    // Malformed escape sequences: fall back to the raw path.
  }
  return decoded
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0 && !STOPWORDS.has(t))
}

function withinOneEdit(a: string, b: string): boolean {
  if (a === b) return true
  if (Math.abs(a.length - b.length) > 1) return false
  let i = 0
  let j = 0
  let edits = 0
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++
      j++
      continue
    }
    if (++edits > 1) return false
    if (a.length > b.length) i++
    else if (b.length > a.length) j++
    else {
      i++
      j++
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1
}

// Exact, or a one-letter typo on a word long enough for that to be safe.
function tokensMatch(a: string, b: string): boolean {
  if (a === b) return true
  return a.length >= 4 && b.length >= 4 && withinOneEdit(a, b)
}

/** Share of distinct tokens the two slugs have in common (fuzzy Jaccard). */
export function similarity(requested: string[], candidate: string[]): number {
  const a = Array.from(new Set(requested))
  const b = Array.from(new Set(candidate))
  if (a.length === 0 || b.length === 0) return 0
  const matched = a.filter((t) => b.some((u) => tokensMatch(t, u))).length
  return matched / (a.length + b.length - matched)
}

/**
 * Up to `limit` candidates scoring at least `minScore`, best first. A small
 * bonus goes to candidates in the same site section as the requested path.
 */
export function closestPages(
  path: string,
  candidates: readonly PageCandidate[],
  limit = 3,
  minScore = 0.3
): ScoredCandidate[] {
  const requested = slugTokens(path)
  if (requested.length === 0) return []
  const section = path.split('/')[1] ?? ''
  return candidates
    .map((c) => {
      const base = similarity(requested, slugTokens(c.href))
      const bonus = base > 0 && c.href.split('/')[1] === section ? 0.05 : 0
      return { ...c, score: base + bonus }
    })
    .filter((c) => c.score >= minScore)
    .sort((x, y) => y.score - x.score || x.href.localeCompare(y.href))
    .slice(0, limit)
}
